import type { ICellRendererComp, ICellRendererParams } from "ag-grid-enterprise";

interface RowAction {
  type?: "badge" | "action";
  label: string;
  url?: string;
  icon?: string;
  method?: string;
  rel?: string;
  confirm?: string;
}

/**
 * ActionsCellRenderer
 *
 * Renders inline action buttons and badges from `row_actions` on the row data.
 * Each item is either a badge (type: 'badge') rendered as styled text, or a
 * link button rendered with the standard btn--blue-stroke style + FA icon.
 *
 * Supports: rel="modal:open", data-method, data-confirm (Rails UJS conventions).
 */
export class ActionsCellRenderer implements ICellRendererComp {
  private eGui!: HTMLElement;

  init(params: ICellRendererParams): void {
    this.eGui = document.createElement("div");
    this.eGui.className = "ag-actions-cell";

    if (params.node.group) return;

    const rowActions = (params.data as Record<string, unknown>)?.row_actions;
    if (!Array.isArray(rowActions) || rowActions.length === 0) return;

    (rowActions as RowAction[]).forEach((action) => {
      if (action.type === "badge") {
        const badge = document.createElement("span");
        badge.className = "ag-actions-cell__badge";
        badge.textContent = action.label;
        this.eGui.appendChild(badge);
      } else {
        const link = document.createElement("a");
        link.className = "btn--small btn btn--blue-stroke btn--with-icon";
        link.href = action.url || "#";

        if (action.method && action.method !== "get") {
          link.dataset.method = action.method;
          link.dataset.remote = "true";
        }
        if (action.rel) {
          link.rel = action.rel;
        }
        if (action.confirm) {
          link.dataset.confirm = action.confirm;
        }

        if (action.icon) {
          const icon = document.createElement("i");
          icon.className = `fas fa-${action.icon}`;
          icon.setAttribute("aria-hidden", "true");
          link.appendChild(icon);
        }

        const label = document.createElement("span");
        label.textContent = action.label;
        link.appendChild(label);

        this.eGui.appendChild(link);
      }
    });
  }

  getGui(): HTMLElement {
    return this.eGui;
  }

  refresh(): boolean {
    return false;
  }
}
